import { EditOutlined } from '@ant-design/icons';
import { Badge, Button, Pagination, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useLazyVacationsQuery, useVacationsQuery } from 'app/services/edpApiV1/vacations';
import { useSubordinatesQuery } from 'app/services/edpApiV1/viewer';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RolesUser, SortCondition } from 'shared/enums';
import { dataForVacationTable, sortingDate } from 'shared/helpers/utils';
import { usePullSort } from 'shared/hooks';
import { PresetStatusColorType } from 'antd/lib/_util/colors';
import SuperviserApproveMarker from 'shared/components/superviserApproveMarker';
import { useIndicators } from 'features/indicators';
import { TPreparedVacationInfoData } from 'shared/types/vacations.types';
import SearchComponent from 'shared/components/search/news-faq';
import { SearchKeysNews } from 'shared/enums/news';
import StringFormItem from 'shared/components/search/news-faq/stringFormItem/StringFormItem';
import NumberFormItem from 'shared/components/search/news-faq/numberFormItem/NumberFormItem';
import { RequestsSearch } from 'shared/types/types';
import { useAppSelector } from 'app/store';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { DayOffsStatusTypesMap } from 'shared/types/dayoff.types';
import { viewerModel } from 'entities/viewer';
import ModalVacationsSubordinates from '../modalVacationsSubordinates';
import style from './index.module.scss';

const VacationsSubordinates = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [dataVacationsSubordinates, setDataVacationsSubordinates] = useState<TPreparedVacationInfoData[] | []>([]);
  const [openModal, setOpenModal] = useState(false);
  const [requestId, setRequestId] = useState<number>();
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [vacationHrId, setVacationHrId] = useState<number | null>(null);
  const [sortInfo, sortTrigger] = usePullSort();
  const location = useLocation();
  const currentLocationState = location?.state as { page: number; size: number } | null;
  const sortOption = sortInfo.value && sortInfo.value === SortCondition.ASCEND ? SortCondition.ASC : SortCondition.DESC;
  const { data: allSubordinates } = useSubordinatesQuery({ include: 'me' });
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);
  const accountInfo = useAppSelector(viewerModel.me.selectors.selectViewerProfile);

  const handleChangeResponsible = (id: number, hrId: number): void => {
    setRequestId(id);
    setOpenModal(true);
    setVacationHrId(hrId);
  };

  const columns: ColumnsType<TPreparedVacationInfoData> = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: () => ({
        onClick: () => sortTrigger(),
      }),
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Вид отпуска',
      dataIndex: 'vacationType',
      key: 'vacationType',
    },
    {
      title: 'Шаг согласования',
      dataIndex: 'applyStep',
      key: 'applyStep',
    },
    {
      title: 'Тип заявления',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Дата начала',
      dataIndex: 'startedAt',
      key: 'startedAt',
      sorter: (a, b) => sortingDate(a.startedAt, b.startedAt),
    },
    {
      title: 'Дата окончания',
      dataIndex: 'endedAt',
      key: 'endedAt',
      sorter: (a, b) => sortingDate(a.endedAt, b.endedAt),
    },
    {
      title: 'Дни',
      dataIndex: 'amountDays',
      key: 'amountDays',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: {
        type?: PresetStatusColorType;
        text: string;
        isSupervisorApproved: boolean;
        steps: number | null;
      }) => (
        <>
          <span className="d-flex">
            <Badge className={style.badge} status={DayOffsStatusTypesMap[status.type || 'processing']} />
            {status.text}
          </span>
          {status.steps && !status.isSupervisorApproved && (
            <>
              <br />
              <SuperviserApproveMarker text="Не согласовано руководителем" />
            </>
          )}
        </>
      ),
    },
  ];

  if (!allAvailableOrganizations && accountInfo.role === RolesUser.HRDirector) {
    columns.push({
      title: 'Ответственный',
      dataIndex: 'hr',
      key: 'hr',
      render: (hr, record: { key: React.Key }) => {
        return (
          <>
            {hr.name || hr.middleName || hr.lastName ? (
              <>
                <span>{`${hr.lastName} ${hr.name} ${hr.middleName ?? ''}`}</span>
                <EditOutlined
                  className={style.btnChangeResponsible}
                  onClick={() => handleChangeResponsible(Number(record.key), hr?.id)}
                />
              </>
            ) : (
              <span
                className={style.changeResponsible}
                onClick={() => handleChangeResponsible(Number(record.key), hr?.id)}
              >
                Назначить
              </span>
            )}
          </>
        );
      },
    });
  }

  const [getVacations, { data, isSuccess, isFetching, isLoading: loadingVacations }] = useLazyVacationsQuery();

  useEffect(() => {
    getVacations({
      type: 'other',
      size: currentLocationState?.size ? currentLocationState.size : 10,
      page: currentLocationState?.page ? currentLocationState.page : 1,
      order: sortInfo.value ? sortOption : undefined,
      allAvailableOrganizations,
      ...search,
    });
  }, [
    currentLocationState?.page,
    currentLocationState?.size,
    currentPage,
    currentSize,
    getVacations,
    search,
    sortInfo.value,
    sortOption,
    allAvailableOrganizations,
  ]);

  const handlePagination = (value: number, pageSize: number) => {
    setCurrentSize(pageSize);
    setCurrentPage(value);
  };

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[]) => {
      setRequestId(Number(selectedRowKeys));
      setShowDetails(true);
    },
  };

  useEffect(() => {
    if (data) {
      const dataTable = dataForVacationTable(data?.data);
      setDataVacationsSubordinates(dataTable);
    }
  }, [data]);

  useIndicators(isSuccess, isFetching);

  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };

  return (
    <>
      <SearchComponent
        selectOptions={{
          [SearchKeysNews.SEARCH_NAME]: {
            name: SearchKeysNews.SEARCH_NAME,
            alias: 'ФИО',
            node: StringFormItem({
              name: SearchKeysNews.SEARCH_NAME,
              placeholder: 'Введите ФИО',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.SEARCH_NAME);
              },
            }),
          },
          [SearchKeysNews.SEARCH_ID]: {
            name: SearchKeysNews.SEARCH_ID,
            alias: 'Номер заявки',
            node: NumberFormItem({
              name: SearchKeysNews.SEARCH_ID,
              placeholder: 'Введите номер заявки',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.SEARCH_ID);
              },
            }),
          },
          [SearchKeysNews.PERSONNEL_NUMBER]: {
            name: SearchKeysNews.PERSONNEL_NUMBER,
            alias: 'ТН',
            node: StringFormItem({
              name: SearchKeysNews.PERSONNEL_NUMBER,
              placeholder: 'Введите ТН',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.PERSONNEL_NUMBER);
              },
            }),
          },
          [SearchKeysNews.POSITION_NAME]: {
            name: SearchKeysNews.POSITION_NAME,
            alias: 'Должность',
            node: StringFormItem({
              name: SearchKeysNews.POSITION_NAME,
              placeholder: 'Введите должность',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.POSITION_NAME);
              },
            }),
          },
          [SearchKeysNews.DIVISION_NAME]: {
            name: SearchKeysNews.DIVISION_NAME,
            alias: 'Подразделение',
            node: StringFormItem({
              name: SearchKeysNews.DIVISION_NAME,
              placeholder: 'Введите подразделение',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.DIVISION_NAME);
              },
            }),
          },
        }}
      />
      {showDetails && requestId && (
        <div className={style.subordinationBtnMore}>
          <Button
            type="default"
            onClick={() =>
              navigate(`/vacation/user/subordination/${requestId}`, { state: { page: currentPage, size: currentSize } })
            }
          >
            Подробнее
          </Button>
        </div>
      )}

      <Table
        scroll={{ x: true }}
        rowSelection={{
          type: 'radio',
          ...rowSelection,
        }}
        dataSource={dataVacationsSubordinates}
        columns={columns}
        pagination={false}
        bordered
      />
      <div className={style.paginationWrap}>
        <Pagination
          defaultCurrent={1}
          pageSize={currentSize}
          current={currentPage}
          showSizeChanger
          total={data?.pagination?.total}
          onChange={(page, pageSize) => handlePagination(page, pageSize)}
        />
      </div>
      {allSubordinates && allSubordinates.length > 0 && requestId && (
        <ModalVacationsSubordinates
          allSubordinates={allSubordinates}
          openModal={openModal}
          setOpenModal={setOpenModal}
          requestId={requestId}
          vacationHrId={vacationHrId}
        />
      )}
    </>
  );
};

export default VacationsSubordinates;
